import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import posthog from '@libs/posthog';
import { POSTHOG_DOMAIN_CHANGE_SURVEY_ID } from '@config/posthogSurveys';
import { useAuth } from '@contexts/AuthContext';
import leoLogo from '@assets/logo-compact-dark.svg';

// Only users who signed up before the domain change should see the popup —
// new users land directly on percuity.ai and don't need the migration notice.
const DOMAIN_CHANGE_CUTOFF = new Date('2026-05-16T00:00:00Z').getTime();

const DomainChangePopup = () => {
  const { currentUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    const signupIso = currentUser?.account_timestamp;
    if (!signupIso) return;
    const signupMs = new Date(signupIso).getTime();
    if (Number.isNaN(signupMs) || signupMs >= DOMAIN_CHANGE_CUTOFF) return;

    posthog.getActiveMatchingSurveys((surveys) => {
      const survey = surveys.find(
        (s: { id: string }) => s.id === POSTHOG_DOMAIN_CHANGE_SURVEY_ID,
      );
      if (!survey) return;

      setVisible(true);
      if (!shownRef.current) {
        shownRef.current = true;
        posthog.capture('survey shown', {
          $survey_id: POSTHOG_DOMAIN_CHANGE_SURVEY_ID,
        });
      }
    }, true);
  }, [currentUser?.account_timestamp]);

  const handleAcknowledge = () => {
    posthog.capture('survey sent', {
      $survey_id: POSTHOG_DOMAIN_CHANGE_SURVEY_ID,
      $survey_response: 'acknowledged',
    });
    setVisible(false);
  };

  const handleDismiss = () => {
    posthog.capture('survey dismissed', {
      $survey_id: POSTHOG_DOMAIN_CHANGE_SURVEY_ID,
    });
    setVisible(false);
  };

  return (
    <Modal
      open={visible}
      onCancel={handleDismiss}
      footer={null}
      closable
      centered
      width={400}
      styles={{
        body: { padding: 0 },
        container: { padding: 0, overflow: 'hidden' },
      }}
    >
      <div className="bg-[var(--color-light400)] h-[120px] flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
          <img src={leoLogo} alt="Leo" width={40} height={40} />
        </div>
      </div>
      <div className="p-6 text-center">
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          We&apos;ve moved to percuity.ai
        </h3>
        <p className="text-sm text-gray-500 mb-5 leading-relaxed">
          Leo is now at <strong>app.percuity.ai</strong>. The old domain
          (leonardo.fibonaccipartner.com) will keep redirecting here, but
          please update your bookmarks to the new URL.
        </p>
        <button
          className="w-full py-3 border-none rounded-lg bg-leo-primary text-white text-[15px] font-semibold cursor-pointer transition-opacity hover:opacity-90"
          onClick={handleAcknowledge}
        >
          Got it
        </button>
      </div>
    </Modal>
  );
};

export default DomainChangePopup;
